import React from 'react';
import classnames from 'classnames';
import _ from 'lodash';
import { useFormContext } from 'react-hook-form';

// components
import { makeStyles } from '@mui/styles';
import {
  TypeAheadBase,
  RegisteredTypeAhead,
} from '../../components/inputs/autocomplete';
import { InputLabelBase } from '../../components/inputs/InputLabelBase';
import { withShowable } from '../../console/_global/lib/withShowable';
import { ManagedTypeAheadBase } from '../../components/inputs';
import { ManagedFetchingAutoComplete } from '../../components/inputs/autocomplete/ManagedFetchingAutoComplete';

// services
import {
  getTeamsForAgency,
  getMyTeams,
  getTeamsForAgentInAgency,
} from '../../teams/TeamsService';

const defaultLabelProps = {
  // style: { padding: 0, fontSize: '1rem' },
};

export const TeamsLookupField = withShowable(
  ({
    show = true,
    defaultOptions,
    label,
    labelProps = defaultLabelProps,
    agencyId,
    ...props
  }) => {
    if (!show) {
      return null;
    }
    const classes = useStyles();
    const inputClasses = classnames({
      [classes.inline]: labelProps.inline,
    });

    const { setValue } = useFormContext();
    const [options, setOptions] = React.useState(defaultOptions);

    function handleChange(newValue) {
      setValue(props.name, newValue);
    }

    const onFetch = React.useCallback(
      ({ input }, callback) => {
        getTeamsForAgency({ agencyId, search: input }).then(({ data }) => {
          callback(
            data.content.map((team) => ({
              label: `${team.name}`,
              value: team.id,
              meta: {
                ...team,
              },
            }))
          );
        });
      },
      [agencyId]
    );

    function onOptionsChange(newOptions) {
      setOptions(newOptions);
    }

    return (
      <>
        <InputLabelBase
          indent
          htmlFor={props.name}
          required={props.required}
          {...labelProps}
        >
          {label || 'Team'}
        </InputLabelBase>
        <TypeAheadBase
          id="teams"
          options={options}
          onChange={handleChange}
          onOptionsChange={onOptionsChange}
          onFetch={onFetch}
          className={inputClasses}
          {...props}
        />
      </>
    );
  }
);

export const ManagedTeamsLookupField = withShowable(
  ({
    show = true,
    defaultOptions,
    label,
    labelProps = defaultLabelProps,
    agencyId,
    ...props
  }) => {
    if (!show) {
      return null;
    }

    const classes = useStyles();

    const inputClasses = classnames({
      [classes.inline]: labelProps.inline,
    });

    const onFetch = React.useCallback(
      _.debounce(({ input }, callback) => {
        getTeamsForAgency({ agencyId, search: input }).then(({ data }) => {
          callback(
            data.content.map((team) => ({
              label: `${team.name}`,
              value: team.id,
              meta: {
                ...team,
              },
            }))
          );
        });
      }, 500),
      [agencyId]
    );

    return (
      <ManagedTypeAheadBase
        label={label}
        onFetch={onFetch}
        className={inputClasses}
        {...props}
      />
    );
  }
);

export const ManagedTeamByAgentAutocomplete = ({ agentId, ...props }) => {
  const queryKey = ['agent-teams-options', agentId];
  function fetchFuncton({ search }) {
    if (!agentId) return () => Promise.resolve([]);

    return getTeamsForAgentInAgency({ userId: agentId, search }).then(
      (res) => res.data.content
    );
  }

  return (
    <ManagedFetchingAutoComplete
      {...props}
      queryKey={queryKey}
      fetchFunc={fetchFuncton}
    />
  );
};

export const ManagedTeamByAgencyAutocomplete = ({ agencyId, ...props }) => {
  const queryKey = ['agency-teams-options', agencyId];
  function fetchFuncton({ search }) {
    if (!agencyId) return () => Promise.resolve([]);

    return getTeamsForAgency({ agencyId, search }).then(
      (res) => res.data.content
    );
  }

  return (
    <ManagedFetchingAutoComplete
      {...props}
      queryKey={queryKey}
      fetchFunc={fetchFuncton}
    />
  );
};

const useStyles = makeStyles(() => ({
  inline: {
    '& input': {
      padding: '6px !important',
    },
  },
}));

export const RegisteredTeamAutoComplete = ({
  label,
  labelProps = defaultLabelProps,
  ...props
}) => {
  const classes = useStyles();
  const inputClasses = classnames({
    [classes.inline]: labelProps.inline,
  });

  const handleSearch = React.useCallback(({ input }, callback) => {
    return getMyTeams({ search: input }).then((response) =>
      callback(
        response.data.content.map((team) => ({
          label: `${team.name}`,
          value: team.id,
          meta: {
            ...team,
          },
        }))
      )
    );
  }, []);

  return (
    <>
      <InputLabelBase
        indent
        htmlFor={props.name}
        required={props.required}
        {...labelProps}
      >
        {label || 'Team'}
      </InputLabelBase>
      <RegisteredTypeAhead
        id="teams"
        className={inputClasses}
        onFetch={handleSearch}
        filterOptions={filterOptions}
        {...props}
      />
    </>
  );
};

function filterOptions(options) {
  return options;
}
